/**
 * Kanban des demandes : une colonne par statut, cartes déplaçables.
 *
 * Le glisser-déposer s'appuie sur l'API native du navigateur plutôt que sur
 * une bibliothèque : cinq colonnes et des cartes simples ne justifient pas une
 * dépendance de plus dans un outil interne, et le natif fonctionne sans
 * JavaScript de repli sur les navigateurs de bureau visés.
 *
 * Le glisser n'est jamais le **seul** moyen de déplacer une carte : il exclut
 * l'usage au clavier et il est pénible sur un écran tactile — or l'admin est
 * consultée depuis un chantier. Chaque carte porte donc aussi un menu
 * « Déplacer vers », qui fait exactement la même chose.
 */
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EmailIcon from "@mui/icons-material/Email";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PhoneIcon from "@mui/icons-material/Phone";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import { Link as LienRoutage } from "react-router";
import {
  LIBELLES_BUDGET,
  LIBELLES_PROJET,
  LIBELLES_STATUT_LEAD,
  type Lead,
  STATUTS_LEAD,
  type StatutLead,
  formaterDate,
  formaterTelephone,
  libelle,
} from "./LeadsDonnees";
import { FONDS_STATUT } from "./LeadsStatut";

type Proprietes = {
  leads: Lead[];
  surDeplacement: (id: number, statut: StatutLead) => void;
  /** Vrai pendant l'écriture : le kanban reste manipulable, mais le dit. */
  enCours: boolean;
};

/** Type MIME propre à l'application : un glisser venu d'ailleurs sera ignoré. */
const MIME_LEAD = "application/x-delsa-lead";

export function LeadsKanban({ leads, surDeplacement, enCours }: Proprietes) {
  const [colonneSurvolee, setColonneSurvolee] = useState<StatutLead | null>(null);
  const [carteSaisie, setCarteSaisie] = useState<number | null>(null);

  function deposer(statut: StatutLead, donnees: string) {
    setColonneSurvolee(null);
    setCarteSaisie(null);

    const id = Number(donnees);
    if (!Number.isInteger(id) || id <= 0) return;

    const lead = leads.find((candidat) => candidat.id === id);
    // Déposer une carte sur sa propre colonne n'est pas une erreur : c'est un
    // geste avorté, et il ne doit rien écrire du tout.
    if (!lead || lead.statut === statut) return;

    surDeplacement(id, statut);
  }

  return (
    <Box
      sx={{
        display: "grid",
        gap: 2,
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, minmax(240px, 1fr))",
          lg: `repeat(${STATUTS_LEAD.length}, minmax(200px, 1fr))`,
        },
        opacity: enCours ? 0.85 : 1,
        transition: "opacity 120ms",
      }}
    >
      {STATUTS_LEAD.map((statut) => {
        const colonne = leads.filter((lead) => lead.statut === statut);

        return (
          <Box
            key={statut}
            onDragOver={(evenement) => {
              // Sans `preventDefault`, le navigateur refuse le dépôt : c'est
              // lui qui déclare la zone réceptive, pas l'attribut.
              evenement.preventDefault();
              evenement.dataTransfer.dropEffect = "move";
              setColonneSurvolee(statut);
            }}
            onDragLeave={() =>
              setColonneSurvolee((actuelle) => (actuelle === statut ? null : actuelle))
            }
            onDrop={(evenement) => {
              evenement.preventDefault();
              deposer(statut, evenement.dataTransfer.getData(MIME_LEAD));
            }}
            sx={{
              backgroundColor: FONDS_STATUT[statut],
              borderRadius: 2,
              border: "2px dashed",
              borderColor: colonneSurvolee === statut ? "secondary.main" : "transparent",
              p: 1.5,
              minHeight: 200,
              transition: "border-color 120ms",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1.5,
                px: 0.5,
              }}
            >
              <Typography variant="h4" component="h2">
                {LIBELLES_STATUT_LEAD[statut]}
              </Typography>
              <Chip size="small" label={colonne.length} />
            </Box>

            <Box
              component="ul"
              aria-label={`Demandes au statut « ${LIBELLES_STATUT_LEAD[statut]} »`}
              sx={{ listStyle: "none", m: 0, p: 0, display: "grid", gap: 1.5 }}
            >
              {colonne.map((lead) => (
                <Carte
                  key={lead.id}
                  lead={lead}
                  saisie={carteSaisie === lead.id}
                  surSaisie={setCarteSaisie}
                  surDeplacement={surDeplacement}
                />
              ))}

              {colonne.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>
                  Aucune demande.
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

type ProprietesCarte = {
  lead: Lead;
  saisie: boolean;
  surSaisie: (id: number | null) => void;
  surDeplacement: (id: number, statut: StatutLead) => void;
};

function Carte({ lead, saisie, surSaisie, surDeplacement }: ProprietesCarte) {
  const [ancre, setAncre] = useState<HTMLElement | null>(null);

  return (
    <Paper
      component="li"
      variant="outlined"
      draggable
      onDragStart={(evenement) => {
        evenement.dataTransfer.setData(MIME_LEAD, String(lead.id));
        evenement.dataTransfer.effectAllowed = "move";
        surSaisie(lead.id);
      }}
      onDragEnd={() => surSaisie(null)}
      sx={{
        p: 1.5,
        cursor: "grab",
        opacity: saisie ? 0.4 : 1,
        "&:active": { cursor: "grabbing" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
        <DragIndicatorIcon
          fontSize="small"
          sx={{ color: "text.secondary", mt: 0.25 }}
          aria-hidden
        />

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Link
            component={LienRoutage}
            to={`/leads/${lead.id}`}
            underline="hover"
            sx={{ fontWeight: 500, display: "block" }}
          >
            {lead.nom}
          </Link>
          <Typography variant="body2" color="text.secondary" noWrap>
            {lead.communeTexte} — {formaterDate(lead.createdAt)}
          </Typography>
        </Box>

        <IconButton
          size="small"
          aria-label={`Actions pour la demande de ${lead.nom}`}
          aria-haspopup="menu"
          onClick={(evenement) => setAncre(evenement.currentTarget)}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Box>

      <Typography variant="body2" sx={{ mt: 1 }}>
        {libelle(LIBELLES_PROJET, lead.typeProjet)}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {libelle(LIBELLES_BUDGET, lead.budget)}
      </Typography>

      <Box sx={{ display: "flex", gap: 0.5, mt: 1 }}>
        <Tooltip title={`Appeler ${formaterTelephone(lead.telephone)}`}>
          <IconButton
            size="small"
            component="a"
            href={`tel:${lead.telephone}`}
            aria-label={`Appeler ${lead.nom} au ${formaterTelephone(lead.telephone)}`}
          >
            <PhoneIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title={lead.email}>
          <IconButton
            size="small"
            component="a"
            href={`mailto:${lead.email}`}
            aria-label={`Écrire à ${lead.nom}`}
          >
            <EmailIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Menu anchorEl={ancre} open={ancre !== null} onClose={() => setAncre(null)}>
        <MenuItem disabled>
          <ListItemText primary="Déplacer vers" />
        </MenuItem>
        {STATUTS_LEAD.filter((statut) => statut !== lead.statut).map((statut) => (
          <MenuItem
            key={statut}
            onClick={() => {
              setAncre(null);
              surDeplacement(lead.id, statut);
            }}
          >
            {LIBELLES_STATUT_LEAD[statut]}
          </MenuItem>
        ))}
      </Menu>
    </Paper>
  );
}
